import React, { forwardRef, useImperativeHandle } from 'react';
import cn from 'classnames';
import find from 'lodash/find';
import { Collapse, Divider } from 'antd';
import { FormItem } from 'components/Form';
import classes from '../../NewOrderModal.module.scss';
import { ListInfo, Title } from 'views/pages/ClientDetail/components/Elements';
import { ACCOUNTS_LIST, CARDS_LIST } from '../../utils';
import { ORDER_SUMMARY_OBJ_KEYS } from '../../constants';
import { useNewOrderModalData } from '../../../../selectorData';
import { getCurrencyValue } from '../../../../../../../utils/number';

const { Panel } = Collapse;

const ConfirmStep = forwardRef(({ form, style, cartOrders = [] }, ref) => {
  const data = form.getFieldsValue();
  const {
    orderTypes,
    orderRequesters,
    detailData,
    accounts,
    cards,
    currentUser,
    orderTypeDetail,
  } = useNewOrderModalData();
  const CONFIRM_STEP_DATA = () => {
    const formData = {
      notes: data.notes,
      company_name: detailData.legal_name,
      order_submitted_by: `${currentUser.first_name ||
        ''} ${currentUser.last_name || ''}`.trim(),
    };
    const orderType = find(orderTypes, {
      value: data.order_type_id,
    });
    if (orderType) {
      formData.order_type = orderType.label;
    }
    const orderRequester = find(orderRequesters, {
      value: data.requested_by,
    });
    if (orderRequester) {
      formData.order_requested_by = orderRequester.label;
    }
    if (data.payment_method_id === 0) {
      if (data.payment_method === 'account') {
        const account = data.account;
        if (account.bank_name && account.account_number) {
          formData.payment_method = [
            {
              label: 'Account',
            },
            {
              label:
                account.bank_name +
                ' ****' +
                account.account_number.substr(
                  account.account_number.length - 4
                ),
            },
          ];
        }
      } else {
        const card = data.card;
        if (card && card.card_number) {
          formData.payment_method = [
            {
              label: 'Credit/Debit Card',
            },
            {
              label: `${
                card.name_on_card ? card.name_on_card : ''
              } ****${card.card_number.substr(card.card_number.length - 4)}`,
            },
          ];
        }
      }
    } else {
      // Try the specified type first; if not found (e.g. payment_method radio
      // was not explicitly set after form reset), search both lists.
      const accountList = ACCOUNTS_LIST(accounts);
      const cardList = CARDS_LIST(cards);
      let paymentMethod;
      let paymentType;
      if (data.payment_method === 'account') {
        paymentMethod = find(accountList, { value: data.payment_method_id });
        paymentType = 'Account';
      } else if (data.payment_method === 'card') {
        paymentMethod = find(cardList, { value: data.payment_method_id });
        paymentType = 'Credit/Debit Card';
      } else {
        // payment_method radio not set — check accounts first, then cards
        paymentMethod = find(accountList, { value: data.payment_method_id });
        if (paymentMethod) {
          paymentType = 'Account';
        } else {
          paymentMethod = find(cardList, { value: data.payment_method_id });
          paymentType = 'Credit/Debit Card';
        }
      }
      if (paymentMethod) {
        formData.payment_method = [
          { label: paymentType },
          { label: paymentMethod.label },
        ];
      }
    }
    return formData;
  };
  useImperativeHandle(ref, () => ({
    validateStep(cb) {
      cb(true, true);
    },
  }));
  return (
    <div
      style={style}
      className={cn(classes.stepContent, classes.paymentWrapper)}
    >
      <div className={classes.paymentInnerContent}>
        {cartOrders.length > 0 ? (
          /* Multi-order summary */
          <>
            <p>
              You are submitting{' '}
              <strong>
                {cartOrders.length + (data.order_type_id ? 1 : 0)} orders
              </strong>
              . Please review before confirming.
            </p>
            <Title text="Order Summary" />
            <Collapse accordion className={classes.cartConfirmCollapse}>
              {cartOrders.map((cartOrder, idx) => {
                const liveValues = form.getFieldsValue();
                const cartPrice =
                  Number(
                    liveValues[`cart_price_${idx}`] ??
                      cartOrder.formValues.price
                  ) || 0;
                const rushChecked =
                  liveValues[`cart_rush_order_${idx}`]?.length ??
                  cartOrder.formValues.rush_order?.length;
                const cartRush = rushChecked
                  ? cartOrder.orderTypeDetail.rush_amount || 0
                  : 0;
                const cartSubtotal = cartPrice + cartRush;
                const requester = find(orderRequesters, {
                  value: cartOrder.formValues.requested_by,
                });
                return (
                  <Panel
                    key={cartOrder.id}
                    header={
                      <span>
                        {idx + 1}. {cartOrder.orderTypeName}
                        {' \u2014 '}
                        <strong>{getCurrencyValue(cartSubtotal)}</strong>
                      </span>
                    }
                  >
                    <div className={classes.cartConfirmPanelRow}>
                      <span>Order Type</span>
                      <span>{cartOrder.orderTypeName}</span>
                    </div>
                    {requester && (
                      <div className={classes.cartConfirmPanelRow}>
                        <span>Requested By</span>
                        <span>{requester.label}</span>
                      </div>
                    )}
                    <div className={classes.cartConfirmPanelRow}>
                      <span>Order Price</span>
                      <span>{getCurrencyValue(cartPrice)}</span>
                    </div>
                    {cartRush > 0 && (
                      <div className={classes.cartConfirmPanelRow}>
                        <span>Rush Order (+{getCurrencyValue(cartRush)})</span>
                        <span>{getCurrencyValue(cartRush)}</span>
                      </div>
                    )}
                    <Divider style={{ margin: '8px 0' }} />
                    <div
                      className={cn(
                        classes.cartConfirmPanelRow,
                        classes.cartConfirmPanelTotal
                      )}
                    >
                      <span>Subtotal</span>
                      <span>{getCurrencyValue(cartSubtotal)}</span>
                    </div>
                    {cartOrder.formValues.notes && (
                      <div className={classes.cartConfirmPanelRow}>
                        <span>Notes</span>
                        <span>{cartOrder.formValues.notes}</span>
                      </div>
                    )}
                  </Panel>
                );
              })}
              {/* Only show current-order panel when an order type is actively selected */}
              {data.order_type_id &&
                (() => {
                  const currentPrice = Number(data.price) || 0;
                  const currentRush = data.rush_order?.length
                    ? orderTypeDetail.rush_amount || 0
                    : 0;
                  const currentSubtotal = currentPrice + currentRush;
                  const currentOrderType = find(orderTypes, {
                    value: data.order_type_id,
                  });
                  const currentRequester = find(orderRequesters, {
                    value: data.requested_by,
                  });
                  return (
                    <Panel
                      key="current"
                      header={
                        <span>
                          {cartOrders.length + 1}. {currentOrderType?.label}
                          {' \u2014 '}
                          <strong>{getCurrencyValue(currentSubtotal)}</strong>
                        </span>
                      }
                    >
                      <div className={classes.cartConfirmPanelRow}>
                        <span>Order Type</span>
                        <span>{currentOrderType?.label}</span>
                      </div>
                      {currentRequester && (
                        <div className={classes.cartConfirmPanelRow}>
                          <span>Requested By</span>
                          <span>{currentRequester.label}</span>
                        </div>
                      )}
                      <div className={classes.cartConfirmPanelRow}>
                        <span>Order Price</span>
                        <span>{getCurrencyValue(currentPrice)}</span>
                      </div>
                      {currentRush > 0 && (
                        <div className={classes.cartConfirmPanelRow}>
                          <span>Rush Order</span>
                          <span>+ {getCurrencyValue(currentRush)}</span>
                        </div>
                      )}
                      <Divider style={{ margin: '8px 0' }} />
                      <div
                        className={cn(
                          classes.cartConfirmPanelRow,
                          classes.cartConfirmPanelTotal
                        )}
                      >
                        <span>Subtotal</span>
                        <span>{getCurrencyValue(currentSubtotal)}</span>
                      </div>
                      {data.notes && (
                        <div className={classes.cartConfirmPanelRow}>
                          <span>Notes</span>
                          <span>{data.notes}</span>
                        </div>
                      )}
                    </Panel>
                  );
                })()}
            </Collapse>

            <div className={classes.paymentOverview}>
              <div className={classes.paymentOverviewInnerContent}>
                <FormItem shouldUpdate>
                  {() => {
                    const liveValues = form.getFieldsValue();
                    const cartTotal = cartOrders.reduce((sum, co, idx) => {
                      const cp =
                        Number(
                          liveValues[`cart_price_${idx}`] ?? co.formValues.price
                        ) || 0;
                      const rushChecked =
                        liveValues[`cart_rush_order_${idx}`]?.length ??
                        co.formValues.rush_order?.length;
                      const cr = rushChecked
                        ? co.orderTypeDetail.rush_amount || 0
                        : 0;
                      return sum + cp + cr;
                    }, 0);
                    const currentPrice = data.order_type_id
                      ? Number(data.price) || 0
                      : 0;
                    const currentRush =
                      data.order_type_id && data.rush_order?.length
                        ? orderTypeDetail.rush_amount || 0
                        : 0;
                    return (
                      <div className={classes.grandTotal}>
                        <h4>Grand Total</h4>
                        <h4>
                          {getCurrencyValue(
                            currentPrice + currentRush + cartTotal
                          )}
                        </h4>
                      </div>
                    );
                  }}
                </FormItem>
              </div>
            </div>

            <div className={classes.orderSummary}>
              <ListInfo
                className={classes.summaryList}
                objKeys={[
                  {
                    label: 'Payment Method',
                    key: 'payment_method',
                    type: 'multiValue',
                    fullWidth: true,
                    labelKey: 'label',
                  },
                  {
                    label: 'Legal Company Name',
                    key: 'company_name',
                    type: 'value',
                    fullWidth: true,
                  },
                  {
                    label: 'Submitted By',
                    key: 'order_submitted_by',
                    type: 'value',
                    fullWidth: true,
                  },
                ]}
                data={CONFIRM_STEP_DATA()}
              />
            </div>
          </>
        ) : (
          /* Single-order summary (no regression) */
          <>
            <div className={classes.orderSummary}>
              <p>
                Let&apos;s confirm the order summary before submitting the
                order.
              </p>
              <Title text="Order Summary" />
              <ListInfo
                className={classes.summaryList}
                objKeys={ORDER_SUMMARY_OBJ_KEYS}
                data={CONFIRM_STEP_DATA()}
              />
            </div>
            <div className={classes.paymentOverview}>
              <div className={classes.paymentOverviewInnerContent}>
                <FormItem shouldUpdate>
                  {() => {
                    const values = form.getFieldsValue();
                    return (
                      <>
                        {values.rush_order && values.rush_order.length > 0 ? (
                          <>
                            <div className={classes.subtotal}>
                              <h4>Order Price</h4>
                              <h4>{getCurrencyValue(Number(values.price))}</h4>
                            </div>
                            <div className={classes.subtotal}>
                              <h4>Rush Order</h4>
                              <h4>
                                + $
                                {orderTypeDetail.rush_amount
                                  ? orderTypeDetail.rush_amount.toFixed(2)
                                  : '0.00'}
                              </h4>
                            </div>
                            <Divider />
                            <div className={classes.grandTotal}>
                              <h4>Total</h4>
                              <h4>
                                {getCurrencyValue(
                                  Number(values.price) +
                                    Number(orderTypeDetail.rush_amount)
                                )}
                              </h4>
                            </div>
                          </>
                        ) : (
                          <div className={classes.grandTotal}>
                            <h4>Total</h4>
                            <h4>{getCurrencyValue(values.price)}</h4>
                          </div>
                        )}
                      </>
                    );
                  }}
                </FormItem>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
});

export default ConfirmStep;
